import { useState, useEffect, useRef, useCallback, useMemo } from 'preact/hooks';
import { ImageCarouselProps } from '../types';

// Constants for image generation (outside component for better performance)
const IMAGE_SIZES = [
  { width: 1024, height: 683 },
  { width: 728, height: 534 },
  { width: 650, height: 450 },
] as const;

const IMAGE_EXTENSIONS = ["avif", "webp", "jpg"] as const;
const EXTENSION_REGEX = /\.(jpg|jpeg|png|webp|avif)$/i;
const SIZE_REGEX = /(\d{2,5})x(\d{2,5})/;

// Helper functions (pure functions, can be memoized)
const generateImageUrls = (baseUrl: string, sizes: typeof IMAGE_SIZES, extensions: typeof IMAGE_EXTENSIONS) => {
  const urlParts = baseUrl.split("?");
  const [basePath, query] = urlParts;
  const version = query ? `?${query}` : "";
  const currentExtension = basePath.match(EXTENSION_REGEX)?.[1] || "jpg";

  return sizes.flatMap(({ width, height }) =>
    extensions.map((ext) => {
      const sizePart = `${width}x${height}`;
      const newBasePath = basePath.replace(EXTENSION_REGEX, `_${sizePart}.${currentExtension}`);
      return query ? `${newBasePath}${version}.${ext}` : `${newBasePath}.${ext}`;
    })
  );
};

const generateImageMediaUrl = (baseUrl: string, sizes: typeof IMAGE_SIZES, extensions: typeof IMAGE_EXTENSIONS) => {
  const urlWithoutExt = baseUrl.replace(EXTENSION_REGEX, "");
  return sizes.flatMap(({ width, height }) =>
    extensions.map((ext) => {
      const sizedUrl = urlWithoutExt.replace(SIZE_REGEX, `${width}x${height}`);
      return `${sizedUrl}.${ext}`;
    })
  );
};

// Memoized URL generator
const generateResponsiveUrls = (baseUrl: string) => {
  const isS3Image = baseUrl.includes("media.giftory.com");
  
  const generatedUrls = isS3Image
    ? generateImageMediaUrl(baseUrl, IMAGE_SIZES, IMAGE_EXTENSIONS)
    : generateImageUrls(baseUrl, IMAGE_SIZES, IMAGE_EXTENSIONS);

  const [mediumAvif, mediumWebp, mediumJpg] = generatedUrls.slice(3, 6);
  const [smallAvif, smallWebp, smallJpg] = generatedUrls.slice(6, 9);

  return {
    smallAvif,
    smallWebp,
    mediumAvif,
    mediumWebp,
    smallJpg,
    mediumJpg,
  };
};

/**
 * ResponsiveImage - SEO and performance optimized image with picture tag
 */
const ResponsiveImage = ({ src, alt, index, onImageLoad }: { src: string; alt: string; index: number; onImageLoad?: (index: number) => void }) => {
  
  // Memoize expensive URL generation - only recalculates when src changes
  const responsiveUrls = useMemo(() => generateResponsiveUrls(src), [src]);

  return (
    <picture className="w-full h-full">
      <source
        srcSet={responsiveUrls.smallAvif}
        type="image/avif"
        media="(max-width: 727px)"
      />
      <source
        srcSet={responsiveUrls.mediumAvif}
        type="image/avif"
        media="(min-width: 728px)"
      />
      <source
        srcSet={responsiveUrls.smallWebp}
        type="image/webp"
        media="(max-width: 727px)"
      />
      <source
        srcSet={responsiveUrls.mediumWebp}
        type="image/webp"
        media="(min-width: 728px)"
      />
      <img 
        src={responsiveUrls.mediumJpg} 
        alt={alt}
        width="414"
        height="436"
                  className="w-full h-full object-cover object-center !block transition-transform duration-300 ease-out group-hover:scale-105"
        loading={index === 0 ? 'eager' : 'lazy'}
        onLoad={() => {
          if (onImageLoad) {
            onImageLoad(index);
          }
        }}
        onError={() => {
          console.error('Image failed to load:', src);
          // Still call onImageLoad to mark as "loaded" even if error
          if (onImageLoad) {
            onImageLoad(index);
          }
        }}
      />
    </picture>
  );
};

/**
 * ImageCarousel - Swiper-based image carousel with performance optimizations
 *
 * **Hover Behavior:**
 * - `Desktop`: Initializes on card hover, destroys on mouse leave
 * - `Mobile`: Initializes when card enters viewport, destroys when leaves
 * - `Not Interactive`: No interactions, static image display only
 */
const ImageCarousel = ({ images = [], title, isCardHovered = false, isNotInteractive = false }: ImageCarouselProps) => {
  const [isSwiperInitialized, setIsSwiperInitialized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set<number>());
  const swiperRef = useRef<HTMLDivElement>(null);
  const swiperInstance = useRef<any>(null);
  const clickHandlersCleanup = useRef<(() => void) | null>(null);

  // Ensure we only work with valid, non-empty image URLs
  const validImages = useMemo(() => 
    Array.isArray(images)
      ? images.filter((img): img is string => typeof img === 'string' && img.trim().length > 0)
      : []
  , [images]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Preload first image on page load
  useEffect(() => {
    if (validImages.length > 0) {
      const img = new Image();
      img.src = validImages[0];
      img.onload = () => {
        setLoadedImages(prev => new Set([...prev, 0]));
      };
      img.onerror = () => {
        // Mark as loaded even if error to prevent infinite retry
        setLoadedImages(prev => new Set([...prev, 0]));
      };
    }
  }, [validImages]);

  // Lazy load image function - memoized to prevent recreation
  const lazyLoadImage = useCallback((index: number) => {
    if (loadedImages.has(index) || index >= validImages.length) return;
    
    const img = new Image();
    img.src = validImages[index];
    img.onload = () => {
      setLoadedImages(prev => new Set([...prev, index]));
    };
    img.onerror = () => {
      // Mark as loaded even if error to prevent infinite retry
      setLoadedImages(prev => new Set([...prev, index]));
    };
  }, [loadedImages, validImages]);

  // Handle image load
  const handleImageLoad = (index: number) => {
    setLoadedImages(prev => new Set([...prev, index]));
  };

  // Update sliding pagination for more than 5 images
  const updateSlidingPagination = useCallback((currentIndex: number, totalImages: number) => {
    const bullets = swiperRef.current?.querySelectorAll('.swiper-pagination-bullet');
    if (!bullets || bullets.length !== 5) return;

    // Calculate sliding window position
    const { startIndex, activeBulletIndex } = calculateSlidingWindow(currentIndex, totalImages);

    // Update each bullet efficiently
    bullets.forEach((bullet, bulletIndex) => {
      const imageIndex = startIndex + bulletIndex;
      const bulletElement = bullet as HTMLElement;
      
      // Update data attribute
      bulletElement.setAttribute('data-image-index', imageIndex.toString());
      
      // Update active state
      bulletElement.classList.toggle('swiper-pagination-bullet-active', bulletIndex === activeBulletIndex);
      
      // Update size based on content availability
      updateBulletSize(bulletElement, bulletIndex, imageIndex, totalImages);
    });
  }, []);

  // Helper: Calculate sliding window position
  const calculateSlidingWindow = (currentIndex: number, totalImages: number) => {
    if (currentIndex <= 3) {
      // Beginning: show images 0-4
      return { startIndex: 0, activeBulletIndex: currentIndex };
    } else if (currentIndex >= totalImages - 2) {
      // End: show last 5 images
      const startIndex = totalImages - 5;
      return { startIndex, activeBulletIndex: currentIndex - startIndex };
    } else {
      // Middle: keep 4th dot as pivot
      return { startIndex: currentIndex - 3, activeBulletIndex: 3 };
    }
  };

  // Helper: Update bullet size efficiently
  const updateBulletSize = (bullet: HTMLElement, bulletIndex: number, imageIndex: number, totalImages: number) => {
    const isEdgeDot = bulletIndex === 0 || bulletIndex === 4;
    const hasContentBeyond = (bulletIndex === 0 && imageIndex > 0) || 
                             (bulletIndex === 4 && imageIndex < totalImages - 1);
    
    // Single DOM operation for size update
    if (isEdgeDot && hasContentBeyond) {
      bullet.className = bullet.className.replace(/!w-1\.5|!h-1\.5/g, '').trim();
      bullet.classList.add('!w-1', '!h-1');
    } else {
      bullet.className = bullet.className.replace(/!w-1(?!\.5)|!h-1(?!\.5)/g, '').trim();
      bullet.classList.add('!w-1.5', '!h-1.5');
    }
  };

  // Setup click handlers for sliding pagination with cleanup
  const setupSlidingPaginationClicks = useCallback((swiperInstance: any) => {
    const bullets = swiperRef.current?.querySelectorAll('.swiper-pagination-bullet');
    if (!bullets) return null;

    // Store handlers for cleanup
    const handlers = new Map<Element, EventListener>();

    bullets.forEach((bullet) => {
      const handler = (e: Event) => {
        e.preventDefault();
        const imageIndex = parseInt((bullet as HTMLElement).getAttribute('data-image-index') || '0');
        if (swiperInstance && !isNaN(imageIndex)) {
          swiperInstance.slideTo(imageIndex);
        }
      };
      
      bullet.addEventListener('click', handler);
      handlers.set(bullet, handler);
    });

    // Return cleanup function
    return () => {
      handlers.forEach((handler, bullet) => {
        bullet.removeEventListener('click', handler);
      });
      handlers.clear();
    };
  }, []);

  // Initialize Swiper
  const initSwiper = useCallback(() => {
    if (typeof window !== 'undefined' && (window as any).Swiper && !isSwiperInitialized && swiperRef.current && !isNotInteractive) {
      try {
        // Lazy load other images
        validImages.forEach((_, index) => {
          if (index > 0) {
            lazyLoadImage(index);
          }
        });

        const swiper = new (window as any).Swiper(swiperRef.current, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: validImages.length > 1,
          autoplay: false,
          allowTouchMove: false, // Disable initially
          initialSlide: Math.min(currentSlideIndex, Math.max(0, validImages.length - 1)),
          pagination: {
            el: '.swiper-pagination',
            clickable: true,
            renderBullet: (index: number, className: string) => {
              const totalImages = validImages.length;
              
              if (totalImages <= 5) {
                // For 5 or fewer images, show all dots same size
                if (index >= totalImages) return '';
                return `<span class="${className} !w-1.5 !h-1.5 bg-white rounded-full shadow-[0px_1px_3px_0px_rgba(0,0,0,0.15)] opacity-60 transition-all duration-300"></span>`;
              } else {
                // For more than 5 images, show sliding pagination (size controlled dynamically)
                if (index >= 5) return '';
                // Initial sizing: first dot normal (no images before initially), last dot small (more images after)
                const isFirstDot = index === 0;
                const isLastDot = index === 4;
                const initialSizeClass = isLastDot ? '!w-1 !h-1' : '!w-1.5 !h-1.5';
                return `<span class="${className} ${initialSizeClass} bg-white rounded-full shadow-[0px_1px_3px_0px_rgba(0,0,0,0.15)] opacity-60 transition-all duration-300" data-image-index="${index}"></span>`;
              }
            },
          },
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
          on: {
            init: () => {
              setIsSwiperInitialized(true);
              setTimeout(() => {
                if (swiper) {
                  swiper.allowTouchMove = true;
                  swiper.update();
                  
                  // Set up sliding pagination for >5 images
                  if (validImages.length > 5) {
                    updateSlidingPagination(currentSlideIndex, validImages.length);
                    clickHandlersCleanup.current = setupSlidingPaginationClicks(swiper);
                  }
                  swiper.on && swiper.on('slideChange', () => {
                    if (swiper) {
                      const currentIndex = swiper.realIndex;
                      setCurrentSlideIndex(currentIndex);
                      const nextIndex = validImages.length > 0 ? (currentIndex + 1) % validImages.length : 0;
                      const prevIndex = validImages.length > 0 ? (currentIndex - 1 + validImages.length) % validImages.length : 0;
                      lazyLoadImage(nextIndex);
                      lazyLoadImage(prevIndex);
                      
                      // Handle sliding pagination for >5 images
                      if (validImages.length > 5) {
                        updateSlidingPagination(currentIndex, validImages.length);
                      }
                    }
                  });
                }
              }, 150);
            }
          }
        });
        
        swiperInstance.current = swiper;
      } catch (error) {
        console.error('Swiper initialization failed:', error);
      }
    }
  }, [validImages, title, isSwiperInitialized, isNotInteractive]);

  // Destroy Swiper
  const destroySwiper = useCallback(() => {
    if (isSwiperInitialized && swiperRef.current) {
      try {
        // Clean up click handlers first
        if (clickHandlersCleanup.current) {
          clickHandlersCleanup.current();
          clickHandlersCleanup.current = null;
        }
        
        const swiperInstance = (swiperRef.current as any).swiper;
        if (swiperInstance) {
          const idx = typeof swiperInstance.realIndex === 'number' ? swiperInstance.realIndex : (swiperInstance.activeIndex || 0);
          setCurrentSlideIndex(idx);
          // Keep styles/transform so current slide remains visible when destroyed
          swiperInstance.destroy(true, false);
        }
        setIsSwiperInitialized(false);
      } catch (error) {
        console.error('Error destroying Swiper:', error);
      }
    }
  }, [title, isSwiperInitialized]);

  // Handle hover for desktop
  useEffect(() => {
    if (!isMobile && validImages.length > 1 && !isNotInteractive) {
      if (isCardHovered) {
        initSwiper();
      } else {
        const timeoutId = setTimeout(() => {
          destroySwiper();
        }, 300);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [isCardHovered, isMobile, validImages.length, isNotInteractive]);

  // Handle viewport intersection for mobile
  useEffect(() => {
    if (isMobile && validImages.length > 1 && !isNotInteractive && swiperRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              initSwiper();
            } else {
              destroySwiper();
            }
          });
        },
        { threshold: 0.1 }
      );
      
      const currentRef = swiperRef.current;
      observer.observe(currentRef);
      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      };
    }
  }, [isMobile, validImages.length, isNotInteractive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up click handlers when component unmounts
      if (clickHandlersCleanup.current) {
        clickHandlersCleanup.current();
        clickHandlersCleanup.current = null;
      }
    };
  }, []);

  return (
    <div className="relative w-full overflow-hidden rounded-t-2xl bg-skeleton-loading" style={{ aspectRatio: '4 / 3', minHeight: '0' }}>
      {/* Skeleton loading background with icon */}
      <div className="absolute inset-0 flex items-center justify-center z-0">
        <svg xmlns="http://www.w3.org/2000/svg" width="46" height="68" viewBox="0 0 46 68" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd"
            d="M39.385 24.4487C39.0028 26.7274 38.3119 28.9244 37.3256 30.9981L36.6245 32.364C35.5545 34.4474 34.3439 36.4791 33.5263 37.8258L33.5131 37.8477L33.5002 37.8695L30.9382 42.1667L30.2476 42.1635L30.52 41.1867C31.0366 39.3659 31.5814 37.3706 32.073 35.3222V35.3257C32.8643 32.2205 33.4025 28.8522 33.6674 25.3635C34.0148 19.382 33.2278 13.4115 31.5302 8.72139C32.5252 9.29401 33.4677 9.96759 34.3368 10.7433C38.0982 14.2414 39.9798 19.3528 39.385 24.4487ZM12.6388 38.1498C10.0021 33.7325 7.27559 29.1649 6.63587 24.5118C6.11104 20.4209 7.16578 16.3397 9.60703 13.0139C10.9334 11.2754 12.5852 9.8409 14.4426 8.76388C14.3969 8.88706 14.3496 9.00595 14.3051 9.13146C12.6954 13.7978 11.9904 19.6132 12.3386 25.3639C12.7189 31.3357 14.2199 36.7169 15.5462 41.4716L15.7225 42.0988L15.0089 42.0953L12.6388 38.1498ZM27.1757 24.9316C26.9748 27.5452 26.6078 30.0532 26.0826 32.4068L25.7458 33.8117C25.2291 35.9645 24.6504 38.0437 24.2596 39.4201L24.2557 39.4345L23.5038 42.1335L23.0443 42.1316L22.4862 42.1261L21.8137 39.7304C20.5131 35.0684 19.1684 30.2477 18.8327 24.9726C18.5381 20.1063 19.127 15.1133 20.4483 11.2684C21.4029 8.58574 22.4428 7.2269 23.0087 6.69638C23.3753 7.04525 23.9392 7.72896 24.5566 8.90811C26.5137 12.8038 27.5156 18.933 27.1757 24.9316ZM38.7246 5.95186C34.4361 2.10141 28.8869 0 23.1732 0C23.1529 0 23.133 0.0015592 23.1126 0.0015592C23.1021 0.0015592 23.0915 0 23.081 0C23.054 0 23.0271 0.003898 23.0001 0.0042878C22.2529 0.0101348 21.5034 0.0475557 20.7531 0.125906C14.2672 0.680592 8.32329 3.95297 4.39353 9.13146C0.94913 13.7978 -0.558919 19.6132 0.186313 25.3639C0.999151 31.3357 4.21181 36.7169 7.0501 41.4716L11.0471 48.1263C11.2112 48.4 11.507 48.5707 11.8244 48.5707L23.0001 48.6206L28.1819 48.6705L28.1855 48.6915C28.23 48.6915 28.273 48.6724 28.314 48.6444L34.0891 48.6705L34.0961 48.6915C34.4209 48.6915 34.7167 48.5208 34.8843 48.2433L39.0919 41.1867C40.1971 39.3659 41.3632 37.3706 42.4148 35.3222V35.3257C44.1089 32.2205 45.2605 28.8522 45.8272 25.3635C46.726 18.1292 44.0839 10.9058 38.7246 5.95186ZM23.7844 67.9827C23.7387 68.0058 22.587 68.0058 22.5468 67.9827C17.4212 67.5197 14.4727 64.1167 14.4727 58.6369C14.4727 54.3265 16.3352 51.6883 19.2028 51.6883C21.1771 51.6883 22.5921 52.9887 23.0771 55.0694C23.1072 55.1618 23.1377 55.2815 23.1666 55.4265C23.1951 55.2815 23.2256 55.1618 23.2561 55.0694C23.7406 52.9887 25.1557 51.6883 27.1299 51.6883C29.9975 51.6883 31.8604 54.3265 31.8604 58.6369C31.8604 64.1167 28.9115 67.5197 23.7844 67.9827Z"
            fill="#FE4F28" />
        </svg>
      </div>
      
      <div ref={swiperRef} className={`swiper product-image-swiper w-full h-full overflow-hidden ${isSwiperInitialized ? 'swiper-initialized' : ''}`}>
        <div className="swiper-wrapper">
          {validImages.map((imgSrc, index) => (
            <div key={index} className="swiper-slide !w-full !h-full">
              <ResponsiveImage
                src={imgSrc}
                alt={`${title} - Image ${index + 1}`}
                index={index}
                onImageLoad={() => {
                  handleImageLoad(index);
                }}
              />
            </div>
          ))}
        </div>
        
        {/* Custom pagination - always visible when not initialized */}
        {validImages.length > 1 && !isSwiperInitialized && !isNotInteractive && (
          <div className="custom-pagination p-1 bg-black/50 rounded-[40px] inline-flex justify-center items-center gap-1">
            {[...Array(Math.min(validImages.length, 5))].map((_, index) => {
              // Calculate active state using same logic as Swiper pagination
              let isActive = false;
              if (validImages.length <= 5) {
                // For 5 or fewer images, direct mapping
                isActive = index === currentSlideIndex;
              } else {
                // For 6+ images, use sliding window logic to match Swiper
                const { startIndex, activeBulletIndex } = calculateSlidingWindow(currentSlideIndex, validImages.length);
                isActive = index === activeBulletIndex;
              }
              
              const isLastDot = index === 4;
              const showMoreIndicator = isLastDot && validImages.length > 5;
              
              return (
                <div 
                  key={index}
                  className={`rounded-full shadow-[0px_1px_3px_0px_rgba(0,0,0,0.15)] transition-all duration-300 ${
                    showMoreIndicator ? 'w-1 h-1' : 'w-1.5 h-1.5'
                  } ${
                    isActive ? 'opacity-95 bg-white' : 'opacity-60 bg-white'
                  }`}
                >
                  <span className="sr-only">{`Image ${index + 1} of ${validImages.length}`}</span>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Swiper pagination */}
        {validImages.length > 1 && (
          <div 
            className={`swiper-pagination ${
              isSwiperInitialized ? 'swiper-pagination-initialized' : ''
            }`}
          />
        )}
        
        {/* Navigation arrows */}
        {validImages.length > 1 && (
          <>
            <div className="swiper-button-prev" />
            <div className="swiper-button-next" />
          </>
        )}
      </div>
    </div>
  );
};

export default ImageCarousel; 